/**
 * Chat-level rules shared by the Workspace widgets (glossary: Active chat,
 * Fan replied). Pure functions over the CRM's `crm_workspace_chat_state`
 * rows, so the widgets, the badge and the tests agree.
 */

export interface ChatTimes {
  last_fan_msg_at: string | null;
  last_out_at: string | null;
}

/** A row of GET /crm/v1/workspace/active: chat state plus my latest send. */
export interface ActiveChat extends ChatTimes {
  account_id: string;
  fan_id: string;
  fan_name: string | null;
  fan_avatar?: string | null;
  last_fan_snippet?: string | null;
  last_out_by: string | null;
  ai_status?: string | null;
  my_last_at: string;
}

export type ActiveWindow = '1h' | 'today' | '24h';
export const ACTIVE_WINDOWS: ActiveWindow[] = ['1h', 'today', '24h'];

function ms(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : -Infinity;
}

/** The fan's latest message is newer than anyone's latest message to them (me, a teammate, the AI). */
export function isFanReplied(c: ChatTimes): boolean {
  return ms(c.last_fan_msg_at) > ms(c.last_out_at);
}

/** Start of the Active chats window. */
export function windowStart(window: ActiveWindow, nowMs: number): number {
  if (window === '1h') return nowMs - 60 * 60 * 1000;
  if (window === '24h') return nowMs - 24 * 60 * 60 * 1000;
  const midnight = new Date(nowMs);
  midnight.setHours(0, 0, 0, 0);
  return midnight.getTime();
}

/** Hours the badge looks back for this window (the socket takes hours). */
export function windowHours(window: ActiveWindow, nowMs: number): number {
  return Math.max(1 / 60, (nowMs - windowStart(window, nowMs)) / 3_600_000);
}

/**
 * Active chats split into Fan replied (longest waiting first — they need an
 * answer) and Waiting on fan (most recently written first).
 */
export function activeChatGroups<T extends ActiveChat>(
  chats: T[],
  { window, nowMs }: { window: ActiveWindow; nowMs: number },
): { replied: T[]; waiting: T[] } {
  const since = windowStart(window, nowMs);
  const inWindow = chats.filter((c) => ms(c.my_last_at) >= since);
  const replied = inWindow.filter(isFanReplied).sort((a, b) => ms(a.last_fan_msg_at) - ms(b.last_fan_msg_at));
  const waiting = inWindow.filter((c) => !isFanReplied(c)).sort((a, b) => ms(b.my_last_at) - ms(a.my_last_at));
  return { replied, waiting };
}

/** A `chat_state` frame from the Workspace socket (crm_workspace_chat_state row). */
export interface ChatStateRow extends ChatTimes {
  account_id: string;
  fan_id: string;
  fan_name?: string | null;
  fan_avatar?: string | null;
  last_fan_snippet?: string | null;
  last_out_by: string | null;
  phase?: string | null;
  phase_at?: string | null;
  ai_status?: string | null;
}

export function chatKey(accountId: string, fanId: string): string {
  return `${accountId}:${fanId}`;
}

/** Fold a live chat_state row into Active chats (only chats already in the list). */
export function applyChatState<T extends ActiveChat>(chats: T[], row: ChatStateRow): T[] {
  const i = chats.findIndex((c) => c.account_id === row.account_id && c.fan_id === row.fan_id);
  if (i < 0) return chats;
  const prev = chats[i]!;
  const next = chats.slice();
  next[i] = {
    ...prev,
    fan_name: row.fan_name || prev.fan_name,
    fan_avatar: row.fan_avatar || prev.fan_avatar,
    last_fan_snippet: row.last_fan_snippet ?? prev.last_fan_snippet,
    last_fan_msg_at: row.last_fan_msg_at,
    last_out_at: row.last_out_at,
    last_out_by: row.last_out_by,
    ai_status: row.ai_status ?? prev.ai_status,
  };
  return next;
}

/** A team_sent event: my own sends make (or refresh) an Active chat. */
export interface SendEvent {
  kind: 'team_sent';
  account_id: string;
  fan_id: string;
  occurred_at: string;
  payload: { operator_id?: string; fan_name?: string | null; fan_avatar?: string | null };
}

export function applySend(chats: ActiveChat[], ev: SendEvent, myUserId: string): ActiveChat[] {
  if (!myUserId || ev.payload?.operator_id !== myUserId) return chats;
  const i = chats.findIndex((c) => c.account_id === ev.account_id && c.fan_id === ev.fan_id);
  const at = ev.occurred_at;
  if (i >= 0) {
    const prev = chats[i]!;
    const next = chats.slice();
    next[i] = {
      ...prev,
      my_last_at: ms(at) > ms(prev.my_last_at) ? at : prev.my_last_at,
      last_out_at: ms(at) > ms(prev.last_out_at) ? at : prev.last_out_at,
      last_out_by: ms(at) > ms(prev.last_out_at) ? myUserId : prev.last_out_by,
    };
    return next;
  }
  return [
    ...chats,
    {
      account_id: ev.account_id,
      fan_id: ev.fan_id,
      fan_name: ev.payload.fan_name ?? null,
      fan_avatar: ev.payload.fan_avatar ?? null,
      last_fan_msg_at: null,
      last_out_at: at,
      last_out_by: myUserId,
      my_last_at: at,
    },
  ];
}
